import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@sara/ui/components/input';

export const SEARCH_DEBOUNCE_MS = 250;

interface CandidateListToolbarValue {
  search?: string;
}

interface CandidateListToolbarProps {
  value: CandidateListToolbarValue;
  onChange: (value: CandidateListToolbarValue) => void;
}

export function CandidateListToolbar({ value, onChange }: CandidateListToolbarProps) {
  const [local, setLocal] = useState(value.search ?? '');
  // Resets `local` to the external value when it changes. Adjusted during render
  // (React's own pattern for this) rather than in an effect, which would render
  // once with the stale `local` before catching up a frame later.
  const [prevValue, setPrevValue] = useState(value.search);
  if (value.search !== prevValue) {
    setPrevValue(value.search);
    setLocal(value.search ?? '');
  }

  useEffect(() => {
    const handle = setTimeout(() => {
      const next = local.trim();
      if (next === (value.search ?? '')) return;
      onChange({ search: next === '' ? undefined : next });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [local, value.search, onChange]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          strokeWidth={1.8}
          aria-hidden
        />
        <Input
          aria-label="Search candidates"
          placeholder="Search by name or email…"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          className="h-9 w-64 rounded-full pl-8 text-[13px]"
        />
      </div>
    </div>
  );
}
